// The INTERACTION layer: everything the player does with a record.
//
// Four things live here, in the order the bundle has them.
//
// 1. The wallet. `CreditsWallet` reads a phone identity from
//    `window.__nspireIdentity`, a `?phone=` query parameter or localStorage. With
//    an identity it talks to the manager at MANAGER_BASE; without one it falls
//    back to a purely local balance in localStorage, seeded at
//    LOCAL_CREDITS_START. `credits` is the single instance the shop uses.
//
// 2. `requestGeneration` — the POST that asks the manager to cut a new song from
//    an album, and `openLibrary`, which opens the studio library in a new tab.
//
// 3. `backCoverTexture` — the procedurally printed back cover used when the real
//    `-back.webp` cannot be fetched: aged paper, the artist and album, a two
//    column track listing, the rights notice, a catalogue number and a barcode.
//    Kept in an LRU of BACK_COVER_CACHE_MAX entries keyed by album id.
//
// 4. `CrateInteractor` — hover picking with a Raycaster, entering a rack and
//    flipping through it, pulling a sleeve out and holding it, turning it over
//    (which slides the disc out), the crate of sleeves carried under the arm, the
//    counter and the buy flow, and the DOM overlays: `#sleevecard` (the record in
//    hand), `#cratewidget` (what is under your arm) and `#tillpanel` (the
//    counter).
//
// The shop only ever plays HELD_DEMO_SECONDS of a track, faded out over the last
// DEMO_FADE seconds.
//
// Recovered from the production bundle. Numbers and strings are byte-identical
// to the shipped source.

import * as THREE from 'three';
import { ROOM } from './room.js';
import { COLLIDERS, PICKABLES, RACKS, COUNTER } from './store.js';
import { Jukebox, playlistIdFor, trackUrl, fetchExistingTracks, hasApi } from './jukebox.js';
import { vinylTexture } from './textures.js';

// How far the crosshair reaches for a pickable sleeve.
const REACH_DISTANCE = 2.1;
// Fallback counter position, used only if COUNTER carries no usable vector.
const TILL_POS = {
    x: ROOM.w / 2 - .62,
    z: 6.9
};
const TILL_RANGE = 2.3;
const OWNED_KEY = "vinylcrate.owned.v1";
const RECORD_PRICE = 125;
const LIBRARY_URL = "https://nspire.studio/library";
const HELD_DEMO_SECONDS = 30;
const DEMO_FADE = 1.4;
const MANAGER_BASE = "https://api.nspire.studio".replace(/\/$/, "");
const LOCAL_CREDITS_KEY = "vinylcrate.credits.local.v1";
const LOCAL_CREDITS_START = 1e3;
// Sleeves drawn stacked under the arm; the crate itself is not capped.
const ARM_SLOTS = 5;
const ARM_POS = new THREE.Vector3(.47, -.238, -.55);

class CreditsWallet {
    constructor() {
        this.cost = RECORD_PRICE;
        this.identity = this._identity();
        this.remote = null;
        this.error = null;
        this.local = this._loadLocal();
        this._inflight = null;
    }

    get isLocal() {
        return !this.identity;
    }

    _identity() {
        try {
            if (typeof window < "u" && window.__nspireIdentity) return String(window.__nspireIdentity);
            if (typeof location < "u") {
                const fromQuery = new URLSearchParams(location.search).get("phone");
                if (fromQuery) {
                    try {
                        localStorage.setItem("vinylcrate.identity", fromQuery);
                    } catch {}
                    return fromQuery;
                }
            }
            return localStorage.getItem("vinylcrate.identity") || null;
        } catch {
            return null;
        }
    }

    _loadLocal() {
        try {
            const stored = localStorage.getItem(LOCAL_CREDITS_KEY);
            const value = stored == null ? NaN : Number(stored);
            if (Number.isFinite(value) && value >= 0) return Math.floor(value);
        } catch {}
        this._saveLocal(LOCAL_CREDITS_START);
        return LOCAL_CREDITS_START;
    }

    _saveLocal(value) {
        this.local = Math.max(0, Math.floor(value));
        try {
            localStorage.setItem(LOCAL_CREDITS_KEY, String(this.local));
        } catch {}
        return this.local;
    }

    balance() {
        return this.isLocal ? this.local : this.remote;
    }

    async refresh() {
        if (this.isLocal || !MANAGER_BASE) return this.balance();
        if (this._inflight) return this._inflight;
        this._inflight = (async () => {
            try {
                const res = await fetch(`${MANAGER_BASE}/api/credits/balance?phone_number=${encodeURIComponent(this.identity)}`, {
                    credentials: "include",
                    signal: AbortSignal.timeout(15e3)
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const body = await res.json();
                const value = body?.balance ?? body?.credits ?? body?.data?.balance;
                if (Number.isFinite(Number(value))) {
                    this.remote = Math.floor(Number(value));
                    this.error = null;
                } else {
                    this.error = "the wallet did not report a balance";
                }
            } catch (err) {
                this.error = String(err?.message || err);
            } finally {
                this._inflight = null;
            }
            return this.balance();
        })();
        return this._inflight;
    }

    async spend(cost, context) {
        const amount = Math.max(0, Math.floor(cost || 0));
        if (this.isLocal) {
            if (this.local < amount) return {
                ok: false,
                local: true,
                balance: this.local,
                error: "not enough credits"
            };
            return {
                ok: true,
                local: true,
                balance: this._saveLocal(this.local - amount)
            };
        }
        if (!MANAGER_BASE) return {
            ok: false,
            local: false,
            balance: this.remote,
            error: "no manager in this build"
        };
        try {
            const res = await fetch(`${MANAGER_BASE}/api/credits/spend`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json"
                },
                signal: AbortSignal.timeout(2e4),
                body: JSON.stringify({
                    phone_number: this.identity,
                    amount: amount,
                    origin: "vinyl-crate-3d",
                    context: context || {}
                })
            });
            const text = await res.text();
            let body = {};
            try {
                body = text ? JSON.parse(text) : {};
            } catch {}
            if (!res.ok || body?.success === false) return {
                ok: false,
                local: false,
                balance: this.remote,
                error: String(body?.error || text.slice(0, 120) || `HTTP ${res.status}`)
            };
            const left = body?.balance ?? body?.remaining ?? body?.credits;
            if (Number.isFinite(Number(left))) this.remote = Math.floor(Number(left));
            else if (this.remote != null) this.remote -= amount;
            return {
                ok: true,
                local: false,
                balance: this.remote
            };
        } catch (err) {
            return {
                ok: false,
                local: false,
                balance: this.remote,
                error: String(err?.message || err)
            };
        }
    }

    // Puts credits back on the local wallet only; returns false on a remote one.
    refund(cost) {
        if (this.isLocal) {
            this._saveLocal(this.local + Math.max(0, Math.floor(cost || 0)));
            return true;
        }
        return false;
    }
}

const credits = new CreditsWallet();

let _sessionId3d = null;

// One id per page load, stamped onto every generate request.
function sessionId3d() {
    if (!_sessionId3d) {
        _sessionId3d = "vc3d-" + Math.floor(Date.now()).toString(36) + "-" +
            (globalThis.crypto?.randomUUID?.().slice(0, 8) || Math.floor(Math.random() * 1e9).toString(36));
    }
    return _sessionId3d;
}

async function requestGeneration(record) {
    if (!MANAGER_BASE) return {
        ok: false,
        error: "no manager configured in this build"
    };
    const artist = record.similarArtist || record.artist || "";
    try {
        const res = await fetch(`${MANAGER_BASE}/api/cratedigging/generate`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            signal: AbortSignal.timeout(3e4),
            body: JSON.stringify({
                albumId: record.id,
                albumTitle: record.title,
                albumArtist: artist,
                referenceArtist: artist,
                albumYear: record.year,
                prompt: record.sonautoPrompt || "",
                tags: artist ? [artist] : [],
                subgenreId: record.subgenreId,
                subgenreLabel: record.subgenreLabel,
                sessionId: sessionId3d()
            })
        });
        const text = await res.text();
        let body = {};
        try {
            body = text ? JSON.parse(text) : {};
        } catch {}
        if (!res.ok || body?.success === false) return {
            ok: false,
            error: String(body?.error || text.slice(0, 120) || `HTTP ${res.status}`)
        };
        return {
            ok: true,
            playlistId: body?.playlistId || playlistIdFor(record.id)
        };
    } catch (err) {
        return {
            ok: false,
            error: String(err?.message || err)
        };
    }
}

function openLibrary() {
    try {
        window.open(LIBRARY_URL, "_blank", "noopener");
    } catch {}
}

// How close, and how squarely faced, a bin has to be before E digs into it.
const RACK_ENTER_RANGE = 2.6;
const RACK_FACING_MIN = .35;
// Walking this far from where browsing started leaves the bin.
const RACK_EXIT_RANGE = 2.9;
// Seconds the camera takes to swing onto the bin when browsing starts.
const SETTLE_TIME = .42;
// Lean, in radians, of the sleeves behind the current one, the current one,
// and the one after it.
const SLEEVE_LEAN_BEHIND = -1.02;
const SLEEVE_LEAN_CURRENT = -.42;
const SLEEVE_LEAN_NEXT = -.13;
// Sleeves further than this from the current index snap rather than animate.
const SLEEVE_WINDOW = 10;
const HELD_POS = new THREE.Vector3(.13, -.1, -.52);

const backCoverCache = new Map;
const BACK_COVER_CACHE_MAX = 24;

// The printed back of a sleeve, drawn when the real back cover will not load.
function backCoverTexture(data) {
    const key = String(data.id || data.album || data.title || Math.random());
    const cached = backCoverCache.get(key);
    if (cached) return cached;
    const size = 768;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#a2977e";
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 2200; i++) {
        const x = Math.random() * size;
        const y = Math.random() * size;
        ctx.fillStyle = `rgba(${120+Math.random()*90},${105+Math.random()*80},${80+Math.random()*60},${.05+Math.random()*.09})`;
        ctx.fillRect(x, y, 1 + Math.random() * 2.4, 1 + Math.random() * 2.4);
    }
    const wash = ctx.createLinearGradient(0, 0, size, size);
    wash.addColorStop(0, "rgba(228,216,188,0.20)");
    wash.addColorStop(1, "rgba(62,50,32,0.28)");
    ctx.fillStyle = wash;
    ctx.fillRect(0, 0, size, size);
    const ink = "#241c12";
    const inkSoft = "rgba(36,28,18,0.62)";
    const margin = 58;
    ctx.fillStyle = ink;
    ctx.font = "600 19px Iowan Old Style, Palatino, Georgia, serif";
    ctx.fillText(String(data.artist || "").toUpperCase(), margin, margin + 16);
    ctx.font = "700 40px Iowan Old Style, Palatino, Georgia, serif";
    const album = String(data.album || data.title || "");
    ctx.fillText(album.length > 22 ? album.slice(0, 21) + "…" : album, margin, margin + 62);
    ctx.strokeStyle = inkSoft;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(margin, margin + 84);
    ctx.lineTo(size - margin, margin + 84);
    ctx.stroke();
    const tracks = Array.isArray(data.tracks) ? data.tracks : [];
    const perSide = Math.ceil(tracks.length / 2) || 1;
    const columnX = [margin, size / 2 + 12];
    for (const side of [0, 1]) {
        const list = side ? tracks.slice(perSide) : tracks.slice(0, perSide);
        let y = margin + 128;
        ctx.fillStyle = ink;
        ctx.font = "700 17px Iowan Old Style, Palatino, Georgia, serif";
        ctx.fillText(side ? "SIDE TWO" : "SIDE ONE", columnX[side], y);
        y += 30;
        ctx.font = "400 19px Iowan Old Style, Palatino, Georgia, serif";
        list.forEach((track, i) => {
            const number = String((side ? perSide : 0) + i + 1).padStart(2, "0");
            ctx.fillStyle = inkSoft;
            ctx.fillText(number, columnX[side], y);
            ctx.fillStyle = ink;
            const title = String(track);
            ctx.fillText(title.length > 21 ? title.slice(0, 20) + "…" : title, columnX[side] + 34, y);
            y += 30;
        });
    }
    ctx.fillStyle = inkSoft;
    ctx.font = "400 14px ui-sans-serif, system-ui, sans-serif";
    const label = String(data.label || "");
    ctx.fillText(`${label}${label&&data.year?"  ·  ":""}${data.year||""}`, margin, size - margin - 42);
    ctx.fillText("ALL RIGHTS OF THE MANUFACTURER AND OF THE OWNER OF THE RECORDED", margin, size - margin - 22);
    ctx.fillText("WORK RESERVED. UNAUTHORISED COPYING, HIRING, LENDING, PUBLIC", margin, size - margin - 4);
    const catalogueNumber = String(data.id || "").toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 16);
    ctx.fillStyle = ink;
    ctx.font = "600 16px ui-monospace, Menlo, monospace";
    ctx.textAlign = "right";
    ctx.fillText(catalogueNumber, size - margin, margin + 16);
    ctx.textAlign = "left";
    const barcodeX = size - margin - 118;
    const barcodeY = size - margin - 62;
    ctx.fillStyle = "#e8e0cc";
    ctx.fillRect(barcodeX - 6, barcodeY - 6, 130, 62);
    ctx.fillStyle = "#1b150e";
    let bar = barcodeX;
    for (; bar < barcodeX + 118;) {
        const width = 1 + Math.floor(Math.random() * 3);
        ctx.fillRect(bar, barcodeY, width, 44);
        bar += width + 1 + Math.floor(Math.random() * 3);
    }
    ctx.strokeStyle = "rgba(70,55,36,0.16)";
    ctx.lineWidth = 9;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2 + 40, size * .33, 0, Math.PI * 2);
    ctx.stroke();
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    if (backCoverCache.size >= BACK_COVER_CACHE_MAX) {
        const oldestKey = backCoverCache.keys().next().value;
        const oldest = backCoverCache.get(oldestKey);
        backCoverCache.delete(oldestKey);
        if (oldest) oldest.dispose();
    }
    backCoverCache.set(key, texture);
    return texture;
}

// PHASE_IN_TIME and PHASE_OUT_TIME are the seconds the pull-out and put-back
// animations take. DISC_SLIDE_TIME divides the disc-slide approach rate.
const PHASE_IN_TIME = .28;
const PHASE_OUT_TIME = .32;
const DISC_SLIDE_TIME = .45;

// A critically damped step, normalised so easeDamped(1) === 1.
const EASE_DAMP_K = 5.6;
const EASE_DAMP_NORM = 1 - (1 + EASE_DAMP_K) * Math.exp(-EASE_DAMP_K);

function easeDamped(t) {
    const x = Math.min(1, Math.max(0, t));
    return (1 - (1 + EASE_DAMP_K * x) * Math.exp(-EASE_DAMP_K * x)) / EASE_DAMP_NORM;
}

// A 0 -> 1 -> 0 hump whose peak sits at t = 0.55.
const ARC_EXP = Math.log(.5) / Math.log(.55);

function arcHump(t) {
    const x = Math.min(1, Math.max(0, t));
    return Math.sin(Math.PI * Math.pow(x, ARC_EXP));
}

function smoothstep01(t) {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
}

// Visits each distinct material on a mesh once, whether it has one or an array.
function forEachMaterial(mesh, fn) {
    const material = mesh.material;
    if (Array.isArray(material)) {
        const seen = new Set;
        for (const m of material) {
            if (!seen.has(m)) {
                seen.add(m);
                fn(m);
            }
        }
    } else if (material) fn(material);
}

class CrateInteractor {
    constructor(camera, scene, hud, player) {
        this.camera = camera;
        this.scene = scene;
        this.hud = hud;
        this.player = player || null;
        this.ray = new THREE.Raycaster();
        this.ray.far = REACH_DISTANCE;
        this.hover = null;
        this.held = null;
        this.flip = 0;
        this.flipT = 0;
        this.t = 0;
        this.phase = "idle";
        this.phaseT = 0;
        this.from = {
            pos: new THREE.Vector3(),
            quat: new THREE.Quaternion()
        };
        this.lag = new THREE.Vector2();
        this.prevYaw = 0;
        this.prevPitch = 0;
        this.outT = 0;
        this.spin = 0;
        this.spinVel = 0;
        this.glowObj = null;
        this.glowK = 0;
        this.fadeObj = null;
        this.fadeK = 0;
        this._v = new THREE.Vector3();
        this._q = new THREE.Quaternion();
        this._q2 = new THREE.Quaternion();
        this._e = new THREE.Euler();
        this.disc = new THREE.Mesh(new THREE.CylinderGeometry(.148, .148, .0016, 48), new THREE.MeshStandardMaterial({
            map: vinylTexture(),
            roughness: .28,
            metalness: .06
        }));
        this.disc.rotation.x = Math.PI / 2;
        this.disc.visible = false;
        scene.add(this.disc);
        this.loader = new THREE.TextureLoader();
        this.texFront = null;
        this.texBack = null;
        this.loadToken = 0;
        this.backAsked = false;
        this._origMat = null;
        this._origUV = null;
        this.frontMat = new THREE.MeshStandardMaterial({
            name: "held:front",
            color: 13816530,
            roughness: .9,
            metalness: 0
        });
        this.backMat = new THREE.MeshStandardMaterial({
            name: "held:back",
            color: 12895428,
            roughness: .93,
            metalness: 0
        });
        this.edgeMat = new THREE.MeshStandardMaterial({
            name: "held:edge",
            color: 13352868,
            roughness: .95,
            metalness: 0
        });
        this.card = this._makeCard();
        this.proxy = null;
        // The stand-in used when the thing under the crosshair is not itself a
        // mesh that can be taken out of the world.
        this.stand = new THREE.Mesh(new THREE.BoxGeometry(.315, .315, .009), [this.edgeMat, this.edgeMat, this.edgeMat, this.edgeMat, this.frontMat, this.backMat]);
        this.stand.visible = false;
        this.stand.castShadow = true;
        scene.add(this.stand);
        this.rack = null;
        this.index = 0;
        this.anim = [];
        this.settle = 0;
        this.settleFrom = null;
        this.settleTo = null;
        this.enterPos = new THREE.Vector3();
        this._racksWarned = false;
        this._normCache = new WeakMap;
        this.jukebox = new Jukebox(state => {
            this.jukeState = state;
            this._refreshCard();
        });
        this.jukeState = null;
        this.crate = [];
        this.owned = this._loadOwned();
        this.counterOpen = false;
        this.paying = false;
        this.receipt = null;
        this.payError = null;
        this.tracks = [];
        this.tracksState = "idle";
        this.tracksFor = null;
        this.tracksErr = null;
        this.toast = null;
        this.demoOver = false;
        this.demoTrack = "";
        this.buying = false;
        this.credits = credits;
        this.crateEl = this._makeCrate();
        this.tillEl = this._makeTill();
        this._makeArm();
        this._armDemoCap();
        this._renderCrate();
        credits.refresh().then(() => this._renderCrate()).catch(() => {});

        addEventListener("keydown", e => {
            if (e.repeat && e.code !== "KeyA" && e.code !== "KeyD" && e.code !== "ArrowLeft" && e.code !== "ArrowRight") return;
            if (this.counterOpen) {
                if (e.code === "Enter" || e.code === "KeyF") {
                    e.preventDefault();
                    this._pay();
                } else if (e.code === "KeyL") {
                    e.preventDefault();
                    openLibrary();
                } else if (e.code === "KeyQ" || e.code === "Escape") {
                    e.preventDefault();
                    this._closeTill();
                }
                return;
            }
            if (e.code === "KeyL") {
                e.preventDefault();
                openLibrary();
                return;
            }
            if (this.held && /^Digit[1-9]$/.test(e.code)) {
                e.preventDefault();
                this._playExistingAt(+e.code.slice(5) - 1);
                return;
            }
            switch (e.code) {
                case "KeyE":
                case "Enter":
                    if (this.held) break;
                    if (this._nearCounter()) {
                        this._openTill();
                        break;
                    }
                    if (this.rack) this.grab(this._currentSleeve());
                    else if (e.code === "KeyE" && !this._enterRack()) this.grab();
                    break;
                case "KeyC":
                    if (this.held) this._addToCrate();
                    break;
                case "KeyR":
                    if (this.held && this.phase !== "drop") {
                        this.flip = 1 - this.flip;
                        this._sfx("turn");
                        if (this.flip === 1) this._loadBack();
                        this._refreshCard();
                    }
                    break;
                case "KeyF":
                    this._playCurrent();
                    break;
                case "KeyQ":
                    if (this.held) this.release();
                    else if (this.rack) this.exitRack();
                    break;
                case "KeyA":
                case "ArrowLeft":
                    if (this.rack && !this.held) {
                        this.step(-1);
                        e.preventDefault();
                    }
                    break;
                case "KeyD":
                case "ArrowRight":
                    if (this.rack && !this.held) {
                        this.step(1);
                        e.preventDefault();
                    }
                    break;
            }
        });

        addEventListener("wheel", e => {
            if (this.held) return;
            // SCROLL THE BIN YOU ARE LOOKING AT, not the one you opened first.
            // This used to take any existing this.rack and keep stepping it, so
            // once you had dug into one crate, turning to another and scrolling
            // carried on flipping through the first one.
            const aimed = this._findRack();
            if (aimed && aimed !== this.rack) {
                if (this.rack) this.exitRack();
                this._startBrowse(aimed);
            } else if (!this.rack && !this._enterRack()) return;
            const raw = e.deltaY || e.deltaX;
            if (!raw) return;
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
            const delta = raw * unit;
            const now = performance.now();
            if (now - (this._wheelAt || 0) > 260) this._wheelAcc = 0;
            this._wheelAt = now;
            this._wheelAcc = (this._wheelAcc || 0) + delta;
            if (Math.abs(this._wheelAcc) < 140) return;
            if (now - (this._stepAt || 0) < 110) {
                this._wheelAcc = 0;
                return;
            }
            this._stepAt = now;
            const dir = this._wheelAcc > 0 ? -1 : 1;
            this._wheelAcc = 0;
            this.step(dir);
        }, {
            passive: true
        });
    }

    _makeArm() {
        this.arm = new THREE.Group();
        this.arm.position.copy(ARM_POS);
        this.camera.add(this.arm);
        this.armGeo = new THREE.BoxGeometry(.2, .2, .009);
        this.armSlots = [];
        for (let i = 0; i < ARM_SLOTS; i++) {
            const material = new THREE.MeshStandardMaterial({
                name: "arm:sleeve",
                color: 12430992,
                roughness: .88,
                metalness: 0,
                emissive: 16777215,
                emissiveIntensity: .46
            });
            const slot = new THREE.Mesh(this.armGeo, material);
            slot.visible = false;
            slot.castShadow = false;
            slot.receiveShadow = false;
            slot.renderOrder = 3;
            this.arm.add(slot);
            this.armSlots.push(slot);
        }
        this._armGoal = 0;
        this._armPop = 0;
    }

    _layoutArm() {
        const shown = Math.min(this.crate.length, ARM_SLOTS);
        for (let i = 0; i < ARM_SLOTS; i++) {
            const slot = this.armSlots[i];
            const used = i < shown;
            slot.visible = used;
            if (!used) {
                if (slot.material.map) {
                    slot.material.map = null;
                    slot.material.emissiveMap = null;
                    slot.material.needsUpdate = true;
                }
                continue;
            }
            const entry = this.crate[this.crate.length - shown + i];
            const tex = entry && entry.armTex;
            if (slot.material.map !== (tex || null)) {
                slot.material.map = tex || null;
                slot.material.emissiveMap = tex || null;
                slot.material.needsUpdate = true;
            }
            slot.position.set(-.003 * i + (i % 2 ? .004 : -.003), .0105 * i, -.005 * i);
            slot.rotation.set(0, 0, (i % 2 ? 1 : -1) * (.04 + .013 * i));
        }
        this._renderCrate();
    }

    _loadArmArt(entry) {
        const url = entry.art;
        if (!url) return;
        this.loader.load(url, tex => {
            tex.colorSpace = THREE.SRGBColorSpace;
            tex.anisotropy = 4;
            if (!this.crate.includes(entry)) {
                tex.dispose();
                return;
            }
            entry.armTex = tex;
            this._layoutArm();
        }, undefined, () => {});
    }

    _disposeArmArt(entry) {
        if (entry && entry.armTex) {
            entry.armTex.dispose();
            entry.armTex = null;
        }
    }

    // 0 where the carried stack would be pushed through a wall or a fixture,
    // 1 out in the open.
    _armClearance() {
        const probe = this._armProbe || (this._armProbe = new THREE.Vector3());
        probe.set(ARM_POS.x, 0, ARM_POS.z - .16);
        this.camera.localToWorld(probe);
        const limitX = ROOM.w / 2 - .08;
        const limitZ = ROOM.d / 2 - .08;
        let nearest = Math.min(limitX - Math.abs(probe.x), limitZ - Math.abs(probe.z));
        for (const box of COLLIDERS) {
            const dx = Math.max(box.minX - probe.x, 0, probe.x - box.maxX);
            const dz = Math.max(box.minZ - probe.z, 0, probe.z - box.maxZ);
            const dist = dx === 0 && dz === 0 ?
                -Math.min(probe.x - box.minX, box.maxX - probe.x, probe.z - box.minZ, box.maxZ - probe.z) :
                Math.hypot(dx, dz);
            if (dist < nearest) nearest = dist;
        }
        return Math.min(1, Math.max(0, nearest / .22));
    }

    _updateArm(dt) {
        if (!this.arm) return;
        const count = this.crate.length;
        this.arm.visible = count > 0;
        if (!count) return;
        const clearance = this._armClearance();
        this.armTuck = this.armTuck == null ? clearance : this.armTuck + (clearance - this.armTuck) * Math.min(1, dt * 12);
        const tuck = this.armTuck;
        this._armPop = Math.max(0, this._armPop - dt * 3.4);
        const pop = Math.sin(this._armPop * Math.PI) * .05;
        const sway = Math.sin(this.t * .7) * .01;
        this.arm.position.set(
            ARM_POS.x - .29 * (1 - tuck) + this.lag.y * -.075 * tuck + sway * tuck,
            ARM_POS.y + .06 * (1 - tuck) + this.lag.x * -.045 * tuck + Math.cos(this.t * .9) * .006 + pop,
            ARM_POS.z + .19 * (1 - tuck));
        this.arm.rotation.set(
            -.3 + this.lag.x * .62 * tuck,
            (.34 + this.lag.y * .55) * tuck + 1.15 * (1 - tuck),
            -.2 + this.lag.y * .85 * tuck);
    }

    _armDemoCap() {
        const audio = this.jukebox && this.jukebox.audio;
        if (!audio) return;
        audio.loop = false;
        audio.addEventListener("timeupdate", () => this._demoWatch());
        audio.addEventListener("playing", () => {
            this.demoOver = false;
            audio.volume = 1;
            this.demoTrack = this.jukebox && this.jukebox.nowPlaying || "";
        });
        audio.addEventListener("ended", () => {
            this.demoOver = true;
            this._refreshCard();
        });
    }

    _demoWatch() {
        const audio = this.jukebox && this.jukebox.audio;
        if (!audio || audio.paused || !audio.src) return;
        const at = audio.currentTime;
        if (at >= HELD_DEMO_SECONDS) {
            audio.pause();
            audio.volume = 1;
            if (!this.demoOver) {
                this.demoOver = true;
                this._say("30-second demo — the full track plays in your studio");
                this._refreshCard();
            }
            return;
        }
        audio.volume = at > HELD_DEMO_SECONDS - DEMO_FADE ? Math.max(0, (HELD_DEMO_SECONDS - at) / DEMO_FADE) : 1;
    }

    _demoElapsed() {
        const audio = this.jukebox && this.jukebox.audio;
        if (!audio || !audio.src) return 0;
        return Math.min(HELD_DEMO_SECONDS, audio.currentTime || 0);
    }

    _demoStrip() {
        if ((this.jukeState && this.jukeState.state) !== "playing" && !this.demoOver) return "";
        const elapsed = this._demoElapsed();
        const fraction = Math.max(0, Math.min(1, elapsed / HELD_DEMO_SECONDS));
        const clock = `0:${String(Math.floor(elapsed)).padStart(2,"0")} / 0:${HELD_DEMO_SECONDS}`;
        return `<div style="display:flex;justify-content:space-between;align-items:baseline;font-size:9.5px;letter-spacing:0.14em;text-transform:uppercase;color:#9c8c6c;margin-top:6px"><span>${this.demoOver?"demo over":"in-store demo"}</span><span style="font-variant-numeric:tabular-nums">${clock}</span></div><div style="height:2px;margin-top:3px;background:rgba(214,188,140,0.14)"><div style="height:2px;width:${(fraction*100).toFixed(1)}%;background:${this.demoOver?"#8a7c60":"#e0cfa8"}"></div></div><div style="font-size:10px;color:#8a7c60;margin-top:4px">` +
            (this.demoOver ? "that is all the shop plays &mdash; the full track is in your studio &middot; <kbd>L</kbd>" : "the shop only plays 30 seconds") + "</div>";
    }

    _makeCard() {
        if (typeof document > "u") return null;
        if (!document.getElementById("sleevecard-style")) {
            const style = document.createElement("style");
            style.id = "sleevecard-style";
            style.textContent = '@keyframes crate-sweep{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}@keyframes crate-pulse{0%,100%{opacity:0.45}50%{opacity:1}}@keyframes crate-rise{0%{transform:translateY(10px) scale(0.985);opacity:0}60%{opacity:1}100%{transform:none;opacity:1}}@keyframes crate-scan{0%{transform:translateY(0)}100%{transform:translateY(220px)}}@keyframes crate-eq{0%,100%{transform:scaleY(0.30)}50%{transform:scaleY(1)}}#sleevecard:before,#sleevecard:after{content:"";position:absolute;width:14px;height:14px;pointer-events:none;border-color:rgba(120,214,255,0.85);border-style:solid}#sleevecard:before{left:-1px;top:-1px;border-width:2px 0 0 2px}#sleevecard:after{right:-1px;bottom:-1px;border-width:0 2px 2px 0}#sleevecard .cc-scan{position:absolute;left:0;right:0;top:0;height:1px;background:linear-gradient(90deg,transparent,rgba(120,214,255,0.5),transparent);animation:crate-scan 3.4s linear infinite;opacity:0.5;pointer-events:none}#sleevecard .cc-eq{display:inline-flex;gap:1.5px;align-items:flex-end;height:9px;width:11px;margin-right:4px;vertical-align:-1px}#sleevecard .cc-eq i{flex:1;background:#78d6ff;height:100%;transform-origin:bottom;animation:crate-eq 0.7s ease-in-out infinite}#sleevecard .cc-eq i:nth-child(2){animation-delay:0.18s}#sleevecard .cc-eq i:nth-child(3){animation-delay:0.36s}#sleevecard .cc-row{display:flex;gap:0.5rem;align-items:center;margin-top:3px;min-width:0;padding:2px 5px;border-left:2px solid rgba(120,214,255,0.22);background:linear-gradient(90deg,rgba(120,214,255,0.07),transparent 70%)}#sleevecard .cc-row.on{border-left-color:#78d6ff;background:linear-gradient(90deg,rgba(120,214,255,0.20),transparent 78%)}#sleevecard .cc-row.dim{border-left-color:rgba(214,188,140,0.16);background:none}#sleevecard kbd{display:inline-block;min-width:1.15em;padding:1px 5px;margin:0 1px;border:1px solid rgba(120,214,255,0.55);border-radius:0;background:rgba(120,214,255,0.12);color:#d6f2ff;font:600 10px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;text-align:center;letter-spacing:0.06em;text-shadow:0 0 6px rgba(120,214,255,0.5);box-shadow:0 0 8px rgba(120,214,255,0.18) inset}#prompt{left:1.6rem !important;bottom:3.2% !important;transform:none !important;max-width:calc(100vw - 3.2rem)}#cratewidget kbd,#tillpanel kbd{display:inline-block;min-width:1.05em;padding:0.5px 4px;margin:0 1px;border:1px solid rgba(214,188,140,0.42);border-radius:2.5px;background:rgba(214,188,140,0.10);color:#f0e2c4;font:600 10px/1.5 ui-sans-serif,system-ui,sans-serif;letter-spacing:0.04em}';
            document.head.appendChild(style);
        }
        const el = document.createElement("div");
        el.id = "sleevecard";
        el.style.cssText = ["position:fixed", "left:1.6rem", "bottom:9%", "width:22.5rem", "max-width:min(30vw + 6rem, calc(100vw - 3rem))", "padding:0.72rem 0.95rem 0.7rem", "background:linear-gradient(160deg,rgba(14,26,34,0.82),rgba(8,13,18,0.88))", "backdrop-filter:blur(7px) saturate(1.15)", "-webkit-backdrop-filter:blur(7px) saturate(1.15)", "border:1px solid rgba(120,214,255,0.22)", "border-left:2px solid rgba(120,214,255,0.75)", "clip-path:polygon(0 0,calc(100% - 14px) 0,100% 14px,100% 100%,0 100%)", "box-shadow:0 10px 34px rgba(0,0,0,0.55),0 0 22px rgba(60,150,200,0.10),inset 0 1px 0 rgba(160,225,255,0.14)", "border-radius:0", "color:#e6f2f8", "overflow:hidden", "font:400 13px/1.45 ui-sans-serif,system-ui,sans-serif", "text-align:left", "pointer-events:none", "opacity:0", "transition:opacity 140ms ease", "z-index:6", "text-shadow:0 1px 2px rgba(0,0,0,0.6)"].join(";");
        document.body.appendChild(el);
        return el;
    }

    _rule(gap) {
        return `<div style="height:1px;margin:${gap||7}px 0 ${(gap||7)-1}px;background:linear-gradient(90deg,rgba(120,214,255,0.42),rgba(120,214,255,0.04))"></div>`;
    }

    _showCard(data, withTracks) {
        if (!this.card) return;
        const esc = v => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const serif = "Iowan Old Style,Palatino Linotype,Palatino,Georgia,serif";
        let html = "";
        const rackLabel = esc(this.rack && this.rack.label || data.subgenre || data.genre || "");
        const position = this.rack && this.rack.sleeves ? `${this.index+1} / ${this.rack.sleeves.length}` : "";
        if (rackLabel || position) {
            html += `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:0.8rem;font:600 9.5px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:0.18em;text-transform:uppercase;color:#78d6ff"><span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${rackLabel}</span><span style="flex:none;font-variant-numeric:tabular-nums;color:#b8a887">${position}</span></div>`;
        }
        html += `<div style="font:600 19px/1.2 ${serif};color:#fff6e4;margin-top:${rackLabel||position?5:0}px;letter-spacing:0.005em">${esc(data.album||data.title)}</div><div style="font-size:12.5px;letter-spacing:0.09em;text-transform:uppercase;color:#dcc79b;margin-top:4px">${esc(data.artist||"")}</div>`;
        const year = data.year ? esc(data.year) : "";
        const label = data.label ? esc(data.label) : "";
        let genre = esc(data.subgenre || data.genre || "");
        if (genre && genre === rackLabel) genre = esc(data.genre && data.genre !== data.subgenre ? data.genre : "");
        if (year || label || genre) {
            html += this._rule() + '<div style="display:flex;justify-content:space-between;align-items:baseline;gap:0.8rem;font-size:11px;color:#9c8c6c"><span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' +
                [year, label].filter(Boolean).join(" &middot; ") +
                `</span><span style="flex:none;font-style:italic;color:#8a7c60">${genre}</span></div>`;
        }
        if (withTracks && Array.isArray(data.tracks) && data.tracks.length) {
            const half = Math.ceil(data.tracks.length / 2);
            const side = (from, to, offset) => data.tracks.slice(from, to).map((track, i) => `<div style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis"><span style="color:#7d7159;font-variant-numeric:tabular-nums">${String(offset+i+1).padStart(2,"0")}</span> ${esc(track)}</div>`).join("");
            html += `<div style="font-size:9.5px;letter-spacing:0.16em;text-transform:uppercase;color:#8a7c60;margin-top:8px">Side A / B</div><div style="display:flex;gap:1.1rem;margin-top:4px;font-size:11px;line-height:1.55;color:#c6b895"><div style="flex:1;min-width:0">${side(0,half,0)}</div><div style="flex:1;min-width:0">${side(half,data.tracks.length,half)}</div></div>`;
        }
        this._cardTitle = String(data.album || data.title || "");
        html += this._tracksBlock();
        html += this._footer();
        html += this._toastLine();
        this.card.innerHTML = '<div class="cc-scan"></div>' + html;
        this._raiseCard();
    }

    _footer() {
        const juke = this._jukeLine();
        if (juke) return juke;
        if (!(this.held || this.rack)) return "";
        const owned = this.held ? this._isOwned(this.held.userData) : false;
        const carried = this.held && this.crate.some(entry => String(entry.id) === String(this._albumId(this.held.userData)));
        let keys = (this.held ? this._playableTracks().length : 0) ? "<kbd>F</kbd> demo it &middot; 30s" : "<kbd>F</kbd> nothing cut yet";
        if (this.held) {
            if (owned) keys += " &nbsp;<kbd>L</kbd> your studio";
            else if (carried) keys += ' &nbsp;<span style="color:#e0cfa8">under your arm</span>';
            else keys += " &nbsp;<kbd>C</kbd> take it";
            keys += " &nbsp;<kbd>R</kbd> turn over";
        }
        let html = this._rule(7) + `<div style="font-size:10.5px;letter-spacing:0.04em;color:#b8a887">${keys}</div>`;
        if (this.held && !owned) html += `<div style="font-size:10px;color:#8a7c60;margin-top:3px">${RECORD_PRICE} credits at the counter cuts you a new song from this sleeve</div>`;
        else if (this.held && owned) html += '<div style="font-size:10px;color:#9fd6a2;margin-top:3px">bought &mdash; the full track is in your studio library</div>';
        return html;
    }

    _jukeLine() {
        const state = this.jukeState;
        if (!state || state.state === "idle") return "";
        const esc = v => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
        const record = state.record || {};
        const working = state.state === "requesting" || state.state === "generating" || state.state === "loading";
        const now = (typeof performance < "u" ? performance.now() : Date.now()) / 1e3;
        if (working) {
            if (!this._jukeT0) this._jukeT0 = now;
        } else this._jukeT0 = 0;
        const elapsed = this._jukeT0 ? Math.max(0, now - this._jukeT0) : 0;
        const clock = `${Math.floor(elapsed/60)}:${String(Math.floor(elapsed%60)).padStart(2,"0")}`;
        const look = {
            requesting: ["#e0cfa8", "asking the shop to cut it"],
            generating: ["#e0cfa8", "pressing"],
            loading: ["#e0cfa8", "dropping the needle"],
            playing: [this.demoOver ? "#c0ab86" : "#9fd6a2", this.demoOver ? "demo finished" : "playing a demo"],
            error: ["#d99a8a", "couldn't play it"],
            unconfigured: ["#c0ab86", "no generator in this build"]
        } [state.state];
        if (!look) return "";
        const albumTitle = esc(record.album || record.title || "");
        let detail = "";
        if (state.state === "playing") {
            const trackName = esc(this.jukebox && this.jukebox.nowPlaying || record.title || "");
            detail = trackName && trackName !== albumTitle ? `${albumTitle} &mdash; ${trackName}` : albumTitle;
        } else if (working) {
            detail = albumTitle && albumTitle === esc(this._cardTitle || "") ? "" : albumTitle;
        } else if (state.state === "error") {
            detail = esc(state.detail || "unknown error");
        } else {
            detail = esc(state.detail || "");
        }
        let html = this._rule(7) + `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:0.7rem;font-size:9.5px;letter-spacing:0.16em;text-transform:uppercase;color:${look[0]}"><span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">` +
            (state.state === "playing" && !this.demoOver ? '<span style="animation:crate-pulse 1.6s ease-in-out infinite">&#9654;</span> ' : "") +
            `${look[1]}</span>` +
            (working ? `<span style="flex:none;font-variant-numeric:tabular-nums;color:#9c8c6c">${clock}</span>` : "") + "</div>";
        if (detail) html += `<div style="font-size:11.5px;color:#d8c7a2;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${detail}</div>`;
        html += this._demoStrip();
        if (working) html += '<div style="position:relative;height:2px;margin-top:6px;overflow:hidden;background:rgba(214,188,140,0.14)"><div style="position:absolute;inset:0;width:33%;background:linear-gradient(90deg,transparent,rgba(224,207,168,0.85),transparent);animation:crate-sweep 1.5s linear infinite"></div></div><div style="font-size:9.5px;color:#8a7c60;margin-top:4px">a pressing takes a few minutes &middot; keep digging</div>';
        return html;
    }

    _refreshCard() {
        if (!this.card) return;
        if (this.held) {
            this._showCard(this.held.userData, this.flip === 1);
            return;
        }
        if (this.rack) {
            const data = this._currentData();
            if (data) this._showCard(data, false);
            return;
        }
        if (this.jukeState && this.jukeState.state !== "idle") {
            this.card.innerHTML = '<div class="cc-scan"></div>' + this._jukeLine();
            this._raiseCard();
        } else this._hideCard();
    }

    _raiseCard() {
        if (!this.card) return;
        if (this.card.style.opacity !== "1") {
            this.card.style.animation = "none";
            this.card.offsetWidth;
            this.card.style.animation = "crate-rise 170ms cubic-bezier(0.16,0.9,0.3,1) both";
        }
        this.card.style.opacity = "1";
    }

    // Put the held record straight back without the drop animation — used when
    // it has just gone under the arm.
    _tuckHeld() {
        if (!this.held) return;
        const home = this.held.userData.home;
        this._dropArt();
        if (this._origMat) {
            this.held.material = this._origMat;
            this._origMat = null;
            this._restoreUV(this.held.geometry);
        }
        this.held.remove(this.disc);
        this.scene.add(this.disc);
        this.disc.visible = false;
        this.camera.remove(this.held);
        if (home && home.parent) {
            home.parent.add(this.held);
            this.held.position.copy(home.pos);
            this.held.rotation.copy(home.rot);
        }
        this.held.scale.set(1, 1, 1);
        if (this.held === this.stand) {
            this.stand.visible = false;
            this._restoreProxy();
        } else this.held.visible = false;
        this.held = null;
        this.phase = "idle";
        this.phaseT = 0;
        this.flip = 0;
        this.flipT = 0;
        this.demoOver = false;
        this._hideCard();
        this.hud.prompt("");
        this.hud.hot(false);
    }

    _hideCard() {
        if (this.card) this.card.style.opacity = "0";
    }

    _loadOwned() {
        try {
            const raw = localStorage.getItem(OWNED_KEY);
            const list = raw ? JSON.parse(raw) : [];
            return new Set(Array.isArray(list) ? list.map(String) : []);
        } catch {
            return new Set;
        }
    }

    _saveOwned() {
        try {
            localStorage.setItem(OWNED_KEY, JSON.stringify([...this.owned]));
        } catch {}
    }

    _isOwned(data) {
        const id = this._albumId(data);
        return id != null && this.owned.has(String(id));
    }

    _albumId(data) {
        if (!data) return null;
        const record = data.record || data;
        if (record.id != null) return record.id;
        if (data.id != null) return data.id;
        return null;
    }

    _esc(v) {
        return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    _panel(id, styles) {
        if (typeof document > "u") return null;
        const el = document.createElement("div");
        el.id = id;
        el.style.cssText = styles.join(";");
        document.body.appendChild(el);
        return el;
    }

    _makeCrate() {
        return this._panel("cratewidget", ["position:fixed", "right:1.4rem", "top:1.4rem", "width:15.5rem", "max-width:calc(100vw - 2.8rem)", "padding:0.6rem 0.72rem 0.55rem", "background:linear-gradient(178deg,rgba(30,23,16,0.90),rgba(16,12,8,0.90))", "border:1px solid rgba(214,188,140,0.30)", "box-shadow:0 6px 22px rgba(0,0,0,0.45),inset 0 0 0 1px rgba(214,188,140,0.07)", "border-radius:2px", "color:#efe6d4", "font:400 12px/1.4 ui-sans-serif,system-ui,sans-serif", "text-align:left", "pointer-events:none", "z-index:6", "opacity:0", "transition:opacity 180ms ease", "text-shadow:0 1px 2px rgba(0,0,0,0.55)"]);
    }

    _creditLine(fontSize) {
        const balance = credits.balance();
        const shown = balance == null ? "&mdash;" : String(balance);
        return `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:0.6rem;font-size:${fontSize||11}px;color:#dcc79b"><span style="letter-spacing:0.14em;text-transform:uppercase;font-size:9.5px;color:#9c8c6c">Credits</span><span style="font-variant-numeric:tabular-nums;color:#e0cfa8">${shown}</span></div>` +
            (credits.isLocal ? '<div style="font-size:9.5px;color:#c9a06a;margin-top:2px">local balance &mdash; not your real nspire wallet</div>' :
                credits.error ? `<div style="font-size:9.5px;color:#d99a8a;margin-top:2px">wallet unreachable &mdash; ${this._esc(credits.error)}</div>` : "");
    }

    _renderCrate() {
        const el = this.crateEl;
        if (!el) return;
        const count = this.crate.length;
        if (!count && !this.owned.size) {
            el.style.opacity = "0";
            return;
        }
        const esc = v => this._esc(v);
        const total = count * RECORD_PRICE;
        let html = `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:0.6rem;font:600 9.5px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:0.18em;text-transform:uppercase;color:#78d6ff"><span>Under your arm</span><span style="font-variant-numeric:tabular-nums;color:#e0cfa8">${count}</span></div>`;
        if (count) {
            const rows = this.crate.slice(-6).reverse().map(entry => `<div style="display:flex;gap:0.45rem;align-items:baseline;margin-top:3px;border-left:2px solid #c8a86a;padding-left:6px;min-width:0"><span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#efe6d4">${esc(entry.album)}</span><span style="flex:none;font-size:10px;color:#9c8c6c;font-variant-numeric:tabular-nums">${RECORD_PRICE}</span></div>`).join("");
            html += `<div style="margin-top:4px">${rows}</div>`;
            if (count > 6) html += `<div style="font-size:10px;color:#8a7c60;margin-top:3px">+ ${count-6} more</div>`;
            html += this._rule(6) + `<div style="display:flex;justify-content:space-between;font-size:11px;color:#dcc79b"><span>to pay</span><span style="font-variant-numeric:tabular-nums">${total} credits</span></div><div style="font-size:10px;color:#8a7c60;margin-top:3px">take it to the counter</div>`;
        } else html += '<div style="font-size:11px;color:#8a7c60;margin-top:3px">empty &mdash; pull a record and press <kbd>C</kbd></div>';
        html += this._rule(6) + this._creditLine();
        if (this.owned.size) html += `<div style="font-size:10px;color:#9c8c6c;margin-top:4px">${this.owned.size} bought &middot; <kbd>L</kbd> studio library</div>`;
        el.innerHTML = html;
        el.style.opacity = "1";
    }

    _addToCrate() {
        const data = this.held && this.held.userData;
        if (!data) return;
        const id = this._albumId(data);
        if (id == null) return;
        if (this.crate.some(entry => String(entry.id) === String(id))) {
            this._say("already under your arm");
            return;
        }
        const record = data.record || data;
        const entry = {
            id: id,
            album: data.album || data.title || record.title || "Untitled",
            artist: data.artist || record.artist || "",
            rec: record,
            art: data.thumbUrl || record.thumb || data.frontUrl || record.img || null,
            armTex: null
        };
        this.crate.push(entry);
        this._loadArmArt(entry);
        this._armPop = 1;
        this._layoutArm();
        const total = this.crate.length * RECORD_PRICE;
        this._say(`under your arm — ${this.crate.length} record${this.crate.length===1?"":"s"}, ${total} credits at the counter`);
        this._refreshCard();
        this._sfx("take");
        this._tuckHeld();
    }

    _say(text, seconds) {
        const now = (typeof performance < "u" ? performance.now() : Date.now()) / 1e3;
        this.toast = {
            text: text,
            until: now + (seconds || 2.6)
        };
        this._refreshCard();
    }

    _toastLine() {
        if (this.toast) {
            if ((typeof performance < "u" ? performance.now() : Date.now()) / 1e3 > this.toast.until) {
                this.toast = null;
                return "";
            }
            return `<div style="font-size:10.5px;color:#e0cfa8;margin-top:5px">${this._esc(this.toast.text)}</div>`;
        }
        return "";
    }

    _counterPos() {
        const counter = COUNTER;
        const pos = counter && (counter.isVector3 || typeof counter.x == "number" ? counter : counter.position);
        return pos && typeof pos.x == "number" && typeof pos.z == "number" ? pos : TILL_POS;
    }

    _nearCounter() {
        if (this.held || this.rack) return false;
        const counter = this._counterPos();
        const here = this.camera.getWorldPosition(this._v);
        return Math.hypot(here.x - counter.x, here.z - counter.z) <= TILL_RANGE;
    }

    _makeTill() {
        return this._panel("tillpanel", ["position:fixed", "left:50%", "top:50%", "transform:translate(-50%,-50%)", "width:27rem", "max-width:calc(100vw - 3rem)", "padding:1rem 1.1rem 0.9rem", "background:linear-gradient(178deg,rgba(32,25,17,0.96),rgba(14,11,7,0.96))", "border:1px solid rgba(214,188,140,0.34)", "box-shadow:0 18px 60px rgba(0,0,0,0.62),inset 0 0 0 1px rgba(214,188,140,0.07)", "border-radius:3px", "color:#efe6d4", "font:400 13px/1.45 ui-sans-serif,system-ui,sans-serif", "text-align:left", "pointer-events:none", "z-index:8", "display:none", "text-shadow:0 1px 2px rgba(0,0,0,0.55)"]);
    }

    _openTill() {
        if (!this.tillEl) return;
        this.counterOpen = true;
        this.payError = null;
        this.tillEl.style.display = "block";
        this._renderTill();
        this.hud.prompt("");
        this.hud.hot(false);
    }

    _closeTill() {
        this.counterOpen = false;
        this.receipt = null;
        this.payError = null;
        if (this.tillEl) this.tillEl.style.display = "none";
    }

    _renderTill() {
        const el = this.tillEl;
        if (!el) return;
        const esc = v => this._esc(v);
        const serif = "Iowan Old Style,Palatino Linotype,Palatino,Georgia,serif";
        let html = '<div style="font-size:9.5px;letter-spacing:0.18em;text-transform:uppercase;color:#9c8c6c">The counter</div>';
        if (this.receipt) {
            const receipt = this.receipt;
            html += `<div style="font:600 20px/1.2 ${serif};color:#fff6e4;margin-top:5px">Sold &mdash; ${receipt.items.length} record${receipt.items.length===1?"":"s"}</div>` +
                this._rule() +
                receipt.items.map(item => `<div style="display:flex;justify-content:space-between;gap:0.8rem;font-size:12px;color:#d8c7a2;margin-top:2px"><span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(item.album)}` +
                    (item.genErr ? `<span style="color:#d99a8a"> &middot; not cut: ${esc(item.genErr)}</span>` : "") +
                    `</span><span style="flex:none;font-variant-numeric:tabular-nums">${RECORD_PRICE}</span></div>`).join("") +
                this._rule() +
                `<div style="display:flex;justify-content:space-between;font-size:13px;color:#fff6e4"><span>paid</span><span style="font-variant-numeric:tabular-nums">${receipt.total} credits</span></div>` +
                this._creditLine(12);
            if (receipt.cut) html += this._rule() + `<div style="font-size:12.5px;color:#9fd6a2">${receipt.cut} song${receipt.cut===1?" is":"s are"} being cut right now.</div><div style="font-size:11.5px;color:#d8c7a2;margin-top:4px">You cannot hear the whole thing in the shop &mdash; it shows up in your <b>studio library</b> when it is pressed.</div><div style="margin-top:7px"><a href="${LIBRARY_URL}" target="_blank" rel="noopener" style="pointer-events:auto;color:#e0cfa8;font-size:12px">${LIBRARY_URL}</a><span style="font-size:10.5px;color:#8a7c60"> &middot; <kbd>L</kbd> open it</span></div>`;
            if (receipt.failed) html += `<div style="font-size:11.5px;color:#d99a8a;margin-top:7px">${receipt.failed} could not be sent to the presser${receipt.refunded?" &mdash; those credits went back":""}.</div>`;
            html += '<div style="font-size:10.5px;color:#8a7c60;margin-top:9px"><kbd>Q</kbd> leave the counter</div>';
            el.innerHTML = html;
            return;
        }
        const count = this.crate.length;
        if (!count) {
            html += `<div style="font:600 20px/1.2 ${serif};color:#fff6e4;margin-top:5px">Nothing to ring up</div><div style="font-size:12px;color:#b8a887;margin-top:6px">Dig through the bins, pull a record out and press <kbd>C</kbd> to carry it. Every record is a song nobody has made yet.</div>` +
                this._rule() + this._creditLine(12) +
                '<div style="font-size:10.5px;color:#8a7c60;margin-top:9px"><kbd>Q</kbd> step away</div>';
            el.innerHTML = html;
            return;
        }
        const total = count * RECORD_PRICE;
        const balance = credits.balance();
        const short = balance != null && balance < total;
        html += `<div style="font:600 20px/1.2 ${serif};color:#fff6e4;margin-top:5px">${count} record${count===1?"":"s"}</div>` + this._rule();
        html += this.crate.map((entry, i) => `<div style="display:flex;justify-content:space-between;gap:0.8rem;align-items:baseline;margin-top:3px"><span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12.5px;color:#efe6d4"><span style="color:#7d7159;font-variant-numeric:tabular-nums">${String(i+1).padStart(2,"0")}</span> ${esc(entry.album)}` +
            (entry.artist ? `<span style="color:#9c8c6c"> &middot; ${esc(entry.artist)}</span>` : "") +
            `</span><span style="flex:none;font-size:12px;color:#dcc79b;font-variant-numeric:tabular-nums">${RECORD_PRICE}</span></div>`).join("");
        html += this._rule() + `<div style="display:flex;justify-content:space-between;font-size:14px;color:#fff6e4"><span>total</span><span style="font-variant-numeric:tabular-nums">${total} credits</span></div><div style="font-size:10px;color:#8a7c60;margin-top:3px">${RECORD_PRICE} credits a record &mdash; each one cuts a new song into your studio library</div>` +
            this._rule() + this._creditLine(12);
        if (short) html += `<div style="font-size:11.5px;color:#d99a8a;margin-top:8px">that is ${total-balance} credits more than you have</div>`;
        if (this.payError) html += `<div style="font-size:11.5px;color:#d99a8a;margin-top:8px">the till refused it &mdash; ${esc(this.payError)}</div>`;
        html += '<div style="font-size:10.5px;color:#b8a887;margin-top:9px">' +
            (this.paying ? '<span style="color:#e0cfa8">ringing it up&hellip;</span>' : `<kbd>Enter</kbd> pay ${total} &nbsp; <kbd>Q</kbd> not yet`) + "</div>";
        el.innerHTML = html;
    }

    // Charge for each record in turn, then ask the manager to cut it. The first
    // charge that fails stops the run.
    async _pay() {
        if (this.paying || this.receipt || !this.crate.length) return;
        this.paying = true;
        this.payError = null;
        this._renderTill();
        const items = [];
        let firstError = null;
        let cut = 0;
        let failed = 0;
        let refunded = 0;
        for (const entry of this.crate.slice()) {
            const charge = await credits.spend(RECORD_PRICE, {
                albumId: entry.id,
                albumTitle: entry.album,
                playlistId: playlistIdFor(entry.id),
                origin: "vinyl-crate-3d",
                reason: "crate-digging generate"
            });
            if (!charge.ok) {
                if (!firstError) firstError = charge.error;
                break;
            }
            const gen = await requestGeneration(entry.rec);
            if (gen.ok) {
                cut++;
                this.owned.add(String(entry.id));
                items.push({
                    ...entry,
                    playlistId: gen.playlistId,
                    genErr: null
                });
            } else {
                failed++;
                if (credits.refund(RECORD_PRICE)) refunded++;
                if (!firstError) firstError = gen.error;
                items.push({
                    ...entry,
                    playlistId: null,
                    genErr: gen.error
                });
            }
        }
        this._saveOwned();
        const sold = new Set(items.map(item => String(item.id)));
        for (const entry of this.crate)
            if (sold.has(String(entry.id))) this._disposeArmArt(entry);
        this.crate = this.crate.filter(entry => !sold.has(String(entry.id)));
        this.paying = false;
        if (items.length) this.receipt = {
            items: items,
            total: items.length * RECORD_PRICE,
            cut: cut,
            failed: failed,
            refunded: refunded,
            local: credits.isLocal
        };
        else this.payError = firstError || "the charge did not go through";
        if (cut > 0) this._sfx("ding");
        this._layoutArm();
        this._renderCrate();
        this._renderTill();
    }

    _loadTracks(data) {
        const id = this._albumId(data);
        this.tracks = [];
        this.tracksErr = null;
        this.tracksFor = id;
        if (id == null || !hasApi()) {
            this.tracksState = "idle";
            return;
        }
        this.tracksState = "loading";
        const token = this.loadToken;
        fetchExistingTracks(id).then(({
            tracks,
            error
        }) => {
            if (token !== this.loadToken) return;
            this.tracks = Array.isArray(tracks) ? tracks : [];
            this.tracksErr = error;
            this.tracksState = error ? "error" : "ready";
            this._refreshCard();
        });
    }

    _playableTracks() {
        return this.tracks.filter(track => !!trackUrl(track));
    }

    _playExistingAt(slot) {
        const data = this.held && this.held.userData;
        if (!data) return;
        const track = this._playableTracks()[slot];
        if (!track) {
            this._say("nothing in that slot yet");
            return;
        }
        const record = data.record || data;
        this.demoOver = false;
        const audio = this.jukebox.audio;
        if (audio) {
            audio.volume = 1;
            audio.loop = false;
        }
        this.jukebox.playExisting(record, track);
        if (audio) try {
            audio.currentTime = 0;
        } catch {}
        this._say(this._isOwned(data) ? "30-second demo — the full one is in your studio" : `30-second demo — ${RECORD_PRICE} credits at the counter cuts you your own`);
    }

    // The CUT / PRESSING list on the sleeve card: what other diggers have
    // already had cut from this album.
    _tracksBlock() {
        if (!this.held) return "";
        const esc = v => this._esc(v);
        const heading = text => `<div style="display:flex;align-items:center;gap:0.4rem;margin-top:9px"><span style="width:5px;height:5px;background:#78d6ff;box-shadow:0 0 7px rgba(120,214,255,0.9);transform:rotate(45deg)"></span><span style="font:600 9.5px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:0.18em;text-transform:uppercase;color:#78d6ff">${text}</span><span style="flex:1;height:1px;background:linear-gradient(90deg,rgba(120,214,255,0.34),transparent)"></span></div>`;
        if (this.tracksState === "loading") return this._rule() + heading("on this record") + '<div style="font-size:11px;color:#9c8c6c;margin-top:3px">checking what the shop has&hellip;</div>';
        if (this.tracksState === "error") return this._rule() + heading("on this record") + `<div style="font-size:11px;color:#d99a8a;margin-top:3px">couldn't reach the shop &mdash; ${esc(this.tracksErr||"unknown")}</div>`;
        if (this.tracksState !== "ready") return "";
        if (!this.tracks.length) return this._rule() + heading("on this record") + `<div style="font-size:11px;color:#b8a887;margin-top:3px">nobody has cut this one yet &mdash; ${RECORD_PRICE} credits and yours is the first</div>`;
        let playable = 0;
        const rows = this.tracks.slice(0, 9).map(track => {
            const ready = !!trackUrl(track);
            const name = esc(track.name || track.title || "untitled");
            if (ready) {
                playable++;
                const nowPlaying = this.jukebox && this.jukeState && this.jukeState.state === "playing" && this.jukebox.nowPlaying === (track.name || track.title);
                return `<div class="cc-row${nowPlaying?" on":""}">` +
                    (nowPlaying ? '<span class="cc-eq"><i></i><i></i><i></i></span>' : `<kbd>${playable}</kbd>`) +
                    `<span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:${nowPlaying?"#d6f2ff":"#cfe4ee"}">${name}</span><span style="flex:none;font:600 9px/1 ui-monospace,Menlo,monospace;letter-spacing:0.12em;color:${nowPlaying?"#78d6ff":"rgba(120,214,255,0.45)"}">${nowPlaying?"PLAYING":"CUT"}</span></div>`;
            }
            return `<div class="cc-row dim"><span style="width:1.35em;flex:none;text-align:center;color:#6d7f88;animation:crate-pulse 1.6s ease-in-out infinite">&#9679;</span><span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#8d9aa2">${name}</span><span style="flex:none;font:600 9px/1 ui-monospace,Menlo,monospace;letter-spacing:0.12em;color:#6d7f88">PRESSING</span></div>`;
        }).join("");
        return this._rule() + heading(`on this record &middot; ${this.tracks.length}`) +
            `<div style="font-size:11.5px;line-height:1.5;margin-top:2px">${rows}</div>` +
            (playable ? '<div style="font:400 10px/1.4 ui-monospace,Menlo,monospace;letter-spacing:0.05em;color:#7f939d;margin-top:5px">PRESS A NUMBER &mdash; 30s DEMO, CUT BY OTHER DIGGERS</div>' : "");
    }

    // Rewrites the sleeve's uv to a full 0..1 quad per face, replacing the
    // atlas-cell uv it carries in the rack.
    _unatlasUV(geometry) {
        const uv = geometry.attributes.uv;
        const quad = [0, 1, 1, 1, 0, 0, 1, 0];
        for (let i = 0; i < uv.count; i++) uv.setXY(i, quad[i % 4 * 2], quad[i % 4 * 2 + 1]);
        uv.needsUpdate = true;
    }

    _restoreUV(geometry) {
        if (!this._origUV) return;
        geometry.attributes.uv.array.set(this._origUV);
        geometry.attributes.uv.needsUpdate = true;
        this._origUV = null;
    }

    // Try each url in turn; call `onExhausted` when none of them loaded.
    _tex(urls, token, onLoad, onExhausted) {
        const list = urls.filter(Boolean);
        const attempt = i => {
            if (token !== this.loadToken) return;
            if (i >= list.length) {
                if (onExhausted) onExhausted();
                return;
            }
            this.loader.load(list[i], tex => {
                if (token !== this.loadToken || !this.held) {
                    tex.dispose();
                    return;
                }
                tex.colorSpace = THREE.SRGBColorSpace;
                tex.anisotropy = 4;
                tex.generateMipmaps = true;
                onLoad(tex);
            }, undefined, () => attempt(i + 1));
        };
        attempt(0);
    }

    _useOwnMaterial() {
        if (!this.held || Array.isArray(this.held.material)) return;
        this._origMat = this.held.material;
        const geometry = this.held.geometry;
        this._origUV = geometry.attributes.uv.array.slice();
        this._unatlasUV(geometry);
        this.held.material = [this.edgeMat, this.edgeMat, this.edgeMat, this.edgeMat, this.frontMat, this.backMat];
    }

    _applyArt() {
        if (!this.held || !this.texFront) return;
        this._useOwnMaterial();
        if (this.frontMat.map !== this.texFront) {
            this.frontMat.map = this.texFront;
            this.frontMat.needsUpdate = true;
        }
        if (this.texBack) {
            if (this.backMat.map !== this.texBack) {
                this.backMat.map = this.texBack;
                this.backMat.color.setHex(12895428);
                this.backMat.needsUpdate = true;
            }
        } else if (this.backMat.map) {
            this.backMat.map = null;
            this.backMat.needsUpdate = true;
        }
    }

    _loadFront() {
        const data = this.held.userData;
        const record = data.record || {};
        const token = this.loadToken;
        // Fetch the back as soon as the record is in your hands, rather than
        // waiting for the flip. A back is ~290 KB at 1024x1024, so asking for it
        // at flip time means standing there holding a blank sleeve while it
        // downloads. _loadBack guards on this.backAsked, so this is safe to call
        // early and the flip just finds it already decoded.
        this._loadBack();
        this._tex([data.frontUrl, record.img, data.thumbUrl, record.thumb], token, tex => {
            if (this.texFront) this.texFront.dispose();
            this.texFront = tex;
            this._applyArt();
        });
    }

    _loadBack() {
        if (!this.held || this.backAsked) return;
        this.backAsked = true;
        const data = this.held.userData;
        const record = data.record || {};
        const token = this.loadToken;
        const small = record.backImg ? String(record.backImg).replace("-back.webp", "-back-sm.webp") : null;
        this._tex([data.backUrl, small, record.backImg], token, tex => {
            this._freeBack();
            this.texBack = tex;
            this._applyArt();
        }, () => {
            if (token !== this.loadToken || !this.held) return;
            this._freeBack();
            this.texBack = backCoverTexture(data);
            this._applyArt();
        });
    }

    _dropArt() {
        this.loadToken++;
        this.backAsked = false;
        this.frontMat.map = null;
        this.backMat.map = null;
        this.backMat.color.setHex(14076850);
        this.frontMat.needsUpdate = true;
        this.backMat.needsUpdate = true;
        if (this.texFront) {
            this.texFront.dispose();
            this.texFront = null;
        }
        this._freeBack();
    }

    // Only a loaded texture is disposed; the printed fallback is a shared CanvasTexture.
    _freeBack() {
        const tex = this.texBack;
        this.texBack = null;
        if (tex && !tex.isCanvasTexture) tex.dispose();
    }

    _applyGlow(object, k) {
        if (!object) return;
        forEachMaterial(object, material => {
            if (material.emissive) {
                material.emissive.setHex(2365968);
                material.emissiveIntensity = .55 * k;
            }
        });
        const scale = 1 + .03 * k;
        object.scale.set(scale, scale, scale);
    }

    _clearGlow(object) {
        if (!object) return;
        forEachMaterial(object, material => {
            if (material.emissive) {
                material.emissive.setHex(0);
                material.emissiveIntensity = 1;
            }
        });
        object.scale.set(1, 1, 1);
    }

    _setHover(object) {
        if (object === this.glowObj) return;
        if (this.fadeObj && this.fadeObj !== object) this._clearGlow(this.fadeObj);
        this.fadeObj = this.glowObj;
        this.fadeK = this.glowK;
        this.glowObj = object;
        this.glowK = 0;
    }

    _homeWorld(pos, quat) {
        const home = this.held.userData.home;
        home.parent.updateMatrixWorld();
        pos.copy(home.pos).applyMatrix4(home.parent.matrixWorld);
        this._e.copy(home.rot);
        quat.setFromEuler(this._e).premultiply(home.parent.getWorldQuaternion(this._q2));
    }

    _toCameraSpace(pos, quat) {
        this.camera.updateMatrixWorld();
        this.camera.worldToLocal(pos);
        quat.premultiply(this.camera.getWorldQuaternion(this._q2).invert());
    }

    grab(sleeve) {
        if (this.held || this.phase === "drop") return;
        let target = sleeve || this.hover;
        if (target && !target.isMesh) target = this._standInFor(target);
        if (!target || !target.isMesh || !target.userData || !target.userData.home) return;
        this.held = target;
        this._sfx("pull");
        this._setHover(null);
        this._clearGlow(this.held);
        this.hover = null;
        this.flip = 0;
        this.flipT = 0;
        this.outT = 0;
        this.spin = 0;
        this.spinVel = 0;
        this.lag.set(0, 0);
        this.held.updateMatrixWorld();
        this.held.getWorldPosition(this.from.pos);
        this.held.getWorldQuaternion(this.from.quat);
        this._toCameraSpace(this.from.pos, this.from.quat);
        this.camera.add(this.held);
        this.held.position.copy(this.from.pos);
        this.held.quaternion.copy(this.from.quat);
        this.held.add(this.disc);
        this.disc.position.set(0, 0, 0);
        this.disc.visible = false;
        const record = this.held.userData.record || this.held.userData;
        if (this.jukebox.record && this.jukebox.record !== record) this.jukebox.stop();
        this.loadToken++;
        this.backAsked = false;
        this._loadFront();
        this._loadTracks(this.held.userData);
        this._refreshCard();
        this.phase = "grab";
        this.phaseT = 0;
    }

    // Puts `stand` at the picked object's world transform, copies its userData,
    // and shrinks the original to 1e-4 while it is held.
    _standInFor(object) {
        if (!object || !object.userData) return null;
        object.updateMatrixWorld?.(true);
        const stand = this.stand;
        stand.visible = true;
        object.getWorldPosition(stand.position);
        object.getWorldQuaternion(stand.quaternion);
        stand.scale.set(1, 1, 1);
        const rest = object.userData.rest;
        stand.userData = {
            ...object.userData,
            home: object.userData.home || (rest ? {
                pos: rest.position.clone(),
                rot: new THREE.Euler().setFromQuaternion(rest.quaternion),
                parent: this.scene
            } : {
                pos: stand.position.clone(),
                rot: new THREE.Euler().setFromQuaternion(stand.quaternion),
                parent: this.scene
            })
        };
        this.proxy = object;
        object.scale.set(1e-4, 1e-4, 1e-4);
        object.updateMatrix?.();
        return stand;
    }

    _restoreProxy() {
        if (!this.proxy) return;
        this.proxy.scale.set(1, 1, 1);
        this.proxy.updateMatrix?.();
        this.proxy = null;
        this.stand.visible = false;
    }

    release() {
        if (!this.held || this.phase === "drop") return;
        this.from.pos.copy(this.held.position);
        this.from.quat.copy(this.held.quaternion);
        this.flip = 0;
        this.demoOver = false;
        this._hideCard();
        this.phase = "drop";
        this.phaseT = 0;
        this._sfx("thock", { delay: .2 });
    }

    _land() {
        const home = this.held.userData.home;
        this._dropArt();
        if (this._origMat) {
            this.held.material = this._origMat;
            this._origMat = null;
            this._restoreUV(this.held.geometry);
        }
        this.held.remove(this.disc);
        this.scene.add(this.disc);
        this.disc.visible = false;
        this.camera.remove(this.held);
        home.parent.add(this.held);
        this.held.position.copy(home.pos);
        this.held.rotation.copy(home.rot);
        this.held.scale.set(1, 1, 1);
        if (this.held === this.stand) {
            this.stand.visible = false;
            this._restoreProxy();
        }
        this.held = null;
        this.phase = "idle";
        this.tracks = [];
        this.tracksState = "idle";
        this.tracksFor = null;
        this._refreshCard();
    }

    _racks() {
        const racks = RACKS;
        return Array.isArray(racks) ? racks : [];
    }

    _vec(v) {
        if (!v) return null;
        if (v.isVector3) return v.clone();
        if (typeof v.x == "number") return new THREE.Vector3(v.x, v.y || 0, v.z || 0);
        return null;
    }

    _normalise(rack) {
        if (!rack || typeof rack != "object") return null;
        const cached = this._normCache.get(rack);
        if (cached !== undefined) return cached;
        const built = this._build(rack);
        if (built) this._normCache.set(rack, built);
        return built;
    }

    // A rack can be described in several shapes; reduce whichever one it is to
    // { sleeves, datas, base, center, normal, pack, label }.
    _build(rack) {
        const entries = rack.sleeves || rack.records || rack.items || rack.meshes || rack.covers;
        if (!Array.isArray(entries) || !entries.length) return null;
        const sleeves = [];
        const datas = [];
        for (const entry of entries) {
            const mesh = entry && entry.isObject3D ? entry : entry && entry.mesh && entry.mesh.isObject3D ? entry.mesh : null;
            const data = entry && entry.record || entry && entry.userData || mesh && mesh.userData || null;
            if (mesh && data) {
                sleeves.push(mesh);
                datas.push(data);
            }
        }
        if (!sleeves.length) return null;
        const base = sleeves.map(mesh => ({
            pos: mesh.position.clone(),
            quat: mesh.quaternion.clone()
        }));
        let center = this._vec(rack.center || rack.position || rack.pos);
        if (center && rack.parent && rack.parent.isObject3D) {
            rack.parent.updateMatrixWorld();
            center.applyMatrix4(rack.parent.matrixWorld);
        }
        if (!center) {
            center = new THREE.Vector3();
            const world = new THREE.Vector3();
            for (const mesh of sleeves) {
                mesh.getWorldPosition(world);
                center.add(world);
            }
            center.divideScalar(sleeves.length);
        }
        // Which way the stack runs, flattened onto the floor plane.
        const pack = base.length > 1 ? base[base.length - 1].pos.clone().sub(base[0].pos) : new THREE.Vector3(1, 0, 0);
        if (pack.lengthSq() < 1e-9) pack.set(1, 0, 0);
        pack.y = 0;
        if (pack.lengthSq() < 1e-9) pack.set(1, 0, 0);
        pack.normalize();
        let normal = this._vec(rack.normal || rack.forward || rack.facing || rack.dir);
        if (normal) normal.y = 0;
        if (!normal || normal.lengthSq() < 1e-6) {
            normal = new THREE.Vector3(0, 0, 1).applyQuaternion(sleeves[0].getWorldQuaternion(this._q2));
            normal.y = 0;
        }
        if (normal.lengthSq() < 1e-6) normal.set(0, 0, 1);
        normal.normalize();
        {
            const parentQuat = sleeves[0].parent ? sleeves[0].parent.getWorldQuaternion(new THREE.Quaternion()) : new THREE.Quaternion();
            if (pack.clone().applyQuaternion(parentQuat).dot(normal) > 0) pack.negate();
        }
        const label = rack.label || rack.title || rack.subgenreLabel || rack.subgenre || rack.genreLabel || rack.genre || "";
        return {
            raw: rack,
            sleeves: sleeves,
            datas: datas,
            base: base,
            center: center,
            normal: normal,
            pack: pack,
            label: label
        };
    }

    // The bin most squarely in front of the camera, scored on facing, how much
    // its front is turned towards you, and distance.
    _findRack() {
        const racks = this._racks();
        if (!racks.length) return null;
        const here = this.camera.getWorldPosition(new THREE.Vector3());
        const look = this.camera.getWorldDirection(new THREE.Vector3());
        look.y = 0;
        if (look.lengthSq() < 1e-6) look.set(0, 0, -1);
        look.normalize();
        let best = null;
        let bestScore = -1 / 0;
        const toRack = new THREE.Vector3();
        for (const rack of racks) {
            const info = this._normalise(rack);
            if (!info) continue;
            toRack.copy(info.center).sub(here);
            const dist = Math.hypot(toRack.x, toRack.z);
            if (dist > RACK_ENTER_RANGE) continue;
            toRack.y = 0;
            if (toRack.lengthSq() < 1e-6) toRack.copy(look);
            toRack.normalize();
            const facing = toRack.dot(look);
            if (facing < RACK_FACING_MIN) continue;
            const squareness = -toRack.dot(info.normal);
            const score = facing * 2 + squareness - dist * .6;
            if (score > bestScore) {
                bestScore = score;
                best = info;
            }
        }
        return best;
    }

    _enterRack() {
        const rack = this._findRack();
        if (rack) {
            this._startBrowse(rack);
            return true;
        }
        return false;
    }

    _startBrowse(rack) {
        this.rack = rack;
        this._sfx("riffle", { volume: .6 });
        this._setHover(null);
        this.hover = null;
        this.hud.hot(false);
        rack.sleeves.forEach((sleeve, i) => {
            if (!sleeve.userData) sleeve.userData = {};
            if (!sleeve.userData.home && sleeve.parent) sleeve.userData.home = {
                pos: rack.base[i].pos.clone(),
                rot: new THREE.Euler().setFromQuaternion(rack.base[i].quat, sleeve.rotation.order),
                parent: sleeve.parent
            };
        });
        this.index = this._nearestIndex(rack);
        this.anim = rack.sleeves.map(() => ({
            lean: 0,
            lift: 0,
            slide: 0,
            vel: 0
        }));
        this.camera.getWorldPosition(this.enterPos);
        if (this.player) {
            const from = this.enterPos;
            const toRack = this._v.copy(rack.center).sub(from);
            const flat = Math.hypot(toRack.x, toRack.z) || 1e-4;
            this.settleFrom = {
                yaw: this.player.yaw,
                pitch: this.player.pitch
            };
            this.settleTo = {
                yaw: Math.atan2(-toRack.x, -toRack.z),
                pitch: Math.max(-.9, Math.min(.35, Math.atan2(toRack.y, flat)))
            };
            let turn = this.settleTo.yaw - this.settleFrom.yaw;
            for (; turn > Math.PI;) turn -= Math.PI * 2;
            for (; turn < -Math.PI;) turn += Math.PI * 2;
            this.settleTo.yaw = this.settleFrom.yaw + turn;
            this.settle = 0;
        } else this.settle = 1;
        this._refreshCard();
    }

    _nearestIndex(rack) {
        const here = this.camera.getWorldPosition(new THREE.Vector3());
        let best = 0;
        let bestDist = 1 / 0;
        const world = new THREE.Vector3();
        for (let i = 0; i < rack.sleeves.length; i++) {
            rack.sleeves[i].getWorldPosition(world);
            const dist = world.distanceToSquared(here);
            if (dist < bestDist) {
                bestDist = dist;
                best = i;
            }
        }
        return best;
    }

    exitRack() {
        if (!this.rack) return;
        const rack = this.rack;
        rack.sleeves.forEach((sleeve, i) => {
            if (sleeve !== this.held) {
                sleeve.position.copy(rack.base[i].pos);
                sleeve.quaternion.copy(rack.base[i].quat);
            }
        });
        this.rack = null;
        this.anim = [];
        this.settle = 1;
        this.settleFrom = this.settleTo = null;
        this.jukebox.stop();
        this._hideCard();
        this.hud.prompt("");
    }

    _currentSleeve() {
        return this.rack && this.rack.sleeves[this.index] || null;
    }

    _currentData() {
        return this.rack && this.rack.datas[this.index] || null;
    }

    // The sounds of digging come from digging.sfx, set by main.js. It may be absent (no
    // WebAudio) or locked (the page has not been clicked yet); either way, silence.
    _sfx(name, options) {
        if (this.sfx) this.sfx.play(name, options);
    }

    step(dir) {
        if (!this.rack) return;
        const count = this.rack.sleeves.length;
        const next = Math.max(0, Math.min(count - 1, this.index + (dir > 0 ? 1 : -1)));
        if (next === this.index) return;
        this.index = next;
        const now = performance.now();
        this._sfx("flip", { volume: now - (this._lastStepAt || 0) < 170 ? .65 : 1 });
        this._lastStepAt = now;
        const anim = this.anim[this.index];
        if (anim) anim.vel = -3.2;
        this._refreshCard();
    }

    _playCurrent() {
        if (!this.held) {
            this._say("pull it out first — E");
            return;
        }
        const data = this.held.userData;
        if (this.tracksState === "loading") {
            this._say("checking what is on it…");
            return;
        }
        if (this._playableTracks().length) {
            this._playExistingAt(0);
            return;
        }
        if (this._isOwned(data)) {
            this._say("yours is still being pressed — it lands in your studio · L");
            return;
        }
        const carried = this.crate.some(entry => String(entry.id) === String(this._albumId(data)));
        this._say(carried ? `nothing on it yet — pay ${RECORD_PRICE} at the counter and yours is the first` : `nothing on it yet — press C to take it, then ${RECORD_PRICE} at the counter`);
    }

    // Where a sleeve sits relative to the one being looked at: the ones already
    // flipped past lie right back, the current one stands up, the next two are
    // part way.
    _target(offset, out) {
        if (offset < 0) {
            out.lean = SLEEVE_LEAN_BEHIND;
            out.lift = -.004;
            out.slide = .03 + Math.min(-offset, 4) * .004;
        } else if (offset === 0) {
            out.lean = SLEEVE_LEAN_CURRENT;
            out.lift = .022;
            out.slide = .052;
        } else if (offset === 1) {
            out.lean = SLEEVE_LEAN_NEXT;
            out.lift = .006;
            out.slide = .016;
        } else if (offset === 2) {
            out.lean = -.04;
            out.lift = .001;
            out.slide = .005;
        } else {
            out.lean = 0;
            out.lift = 0;
            out.slide = 0;
        }
        return out;
    }

    _animate(dt) {
        const rack = this.rack;
        if (!rack) return;
        const axis = this._v.set(0, 1, 0).cross(rack.pack);
        if (axis.lengthSq() < 1e-9) axis.set(1, 0, 0);
        axis.normalize();
        const first = Math.max(0, this.index - SLEEVE_WINDOW);
        const last = Math.min(rack.sleeves.length - 1, this.index + SLEEVE_WINDOW);
        const target = this._tgt || (this._tgt = {
            lean: 0,
            lift: 0,
            slide: 0
        });
        const k = Math.min(1, dt * 13);
        const lean = this._q2;
        for (let i = 0; i < rack.sleeves.length; i++) {
            const sleeve = rack.sleeves[i];
            if (!sleeve || sleeve === this.held) continue;
            const anim = this.anim[i];
            if (!anim) continue;
            this._target(i - this.index, target);
            if (i < first || i > last) {
                if (anim.lean === target.lean && anim.slide === target.slide) continue;
                anim.lean = target.lean;
                anim.lift = target.lift;
                anim.slide = target.slide;
                anim.vel = 0;
            } else {
                anim.vel += (target.lean - anim.lean) * dt * 90;
                anim.vel *= Math.pow(9e-4, dt);
                anim.lean += anim.vel * dt;
                anim.lift += (target.lift - anim.lift) * k;
                anim.slide += (target.slide - anim.slide) * k;
            }
            const rest = rack.base[i];
            lean.setFromAxisAngle(axis, anim.lean);
            sleeve.quaternion.copy(rest.quat).premultiply(lean);
            sleeve.position.copy(rest.pos);
            sleeve.position.addScaledVector(rack.pack, -anim.slide);
            sleeve.position.y += anim.lift;
        }
    }

    // The held record trails the look direction a little.
    _handLag(dt) {
        const yaw = this.camera.rotation.y;
        const pitch = this.camera.rotation.x;
        let dYaw = yaw - this.prevYaw;
        if (dYaw > Math.PI) dYaw -= Math.PI * 2;
        else if (dYaw < -Math.PI) dYaw += Math.PI * 2;
        const dPitch = pitch - this.prevPitch;
        this.prevYaw = yaw;
        this.prevPitch = pitch;
        const k = Math.min(1, dt * 11);
        this.lag.x += -this.lag.x * k - dPitch * .55;
        this.lag.y += -this.lag.y * k - dYaw * .55;
        const limit = .3;
        this.lag.x = Math.max(-limit, Math.min(limit, this.lag.x));
        this.lag.y = Math.max(-limit, Math.min(limit, this.lag.y));
    }

    _holdPose(pos, quat) {
        const breath = Math.sin(this.t * Math.PI * 2 * .4);
        pos.set(
            HELD_POS.x + Math.sin(this.t * .6) * .012 + this.lag.y * -.06,
            HELD_POS.y + Math.cos(this.t * .8) * .01 + breath * .012,
            HELD_POS.z + breath * .006);
        this._e.set(
            Math.sin(this.t * .9) * .035 + this.lag.x,
            this.flipT + this.lag.y,
            Math.cos(this.t * .7) * .028 + this.lag.y * .35, "YXZ");
        quat.setFromEuler(this._e);
    }

    update(dt) {
        this.t += dt;
        if (this.counterOpen) {
            this.hud.prompt("");
            this.hud.hot(false);
            return;
        }
        if (this.toast && (typeof performance < "u" ? performance.now() : Date.now()) / 1e3 > this.toast.until) {
            this.toast = null;
            this._refreshCard();
        }
        if (!this.player && typeof window < "u" && window.__store) this.player = window.__store.player;
        this._handLag(dt);
        this._updateArm(dt);
        this._demoWatch();
        const k = Math.min(1, dt / .12);
        if (this.glowObj) {
            this.glowK += (1 - this.glowK) * k;
            this._applyGlow(this.glowObj, this.glowK);
        }
        if (this.fadeObj) {
            this.fadeK += (0 - this.fadeK) * k;
            if (this.fadeK < .02) {
                this._clearGlow(this.fadeObj);
                this.fadeObj = null;
            } else this._applyGlow(this.fadeObj, this.fadeK);
        }
        if (this.jukeState && (this.jukeState.state === "requesting" || this.jukeState.state === "generating" || this.jukeState.state === "loading")) {
            this._tick = (this._tick || 0) + dt;
            if (this._tick > .5) {
                this._tick = 0;
                this._refreshCard();
            }
        }
        if (this.rack) {
            if (this.camera.getWorldPosition(this._v2 || (this._v2 = new THREE.Vector3())).distanceTo(this.enterPos) > RACK_EXIT_RANGE && !this.held) this.exitRack();
            else {
                if (this.settle < 1 && this.player && this.settleTo) {
                    this.settle = Math.min(1, this.settle + dt / SETTLE_TIME);
                    const s = smoothstep01(this.settle);
                    this.player.yaw = this.settleFrom.yaw + (this.settleTo.yaw - this.settleFrom.yaw) * s;
                    this.player.pitch = this.settleFrom.pitch + (this.settleTo.pitch - this.settleFrom.pitch) * s;
                }
                this._animate(dt);
            }
        }
        if (this.held) {
            const flipGoal = this.flip * Math.PI;
            this.flipT += (flipGoal - this.flipT) * Math.min(1, dt * 9);
            const turning = Math.abs(flipGoal - this.flipT) > .02;
            const pos = this._v;
            const quat = this._q;
            if (this.phase === "grab") {
                this.phaseT = Math.min(1, this.phaseT + dt / PHASE_IN_TIME);
                const e = easeDamped(this.phaseT);
                this._holdPose(pos, quat);
                this.held.position.lerpVectors(this.from.pos, pos, e);
                this.held.position.y += arcHump(this.phaseT) * .09;
                this.held.position.z -= arcHump(this.phaseT) * .03;
                this.held.quaternion.slerpQuaternions(this.from.quat, quat, e);
                if (this.phaseT >= 1) this.phase = "hold";
            } else if (this.phase === "drop") {
                this.phaseT = Math.min(1, this.phaseT + dt / PHASE_OUT_TIME);
                const e = easeDamped(this.phaseT);
                this._homeWorld(pos, quat);
                this._toCameraSpace(pos, quat);
                this.held.position.lerpVectors(this.from.pos, pos, e);
                this.held.position.y += arcHump(this.phaseT) * .05;
                this.held.quaternion.slerpQuaternions(this.from.quat, quat, e);
                if (this.phaseT >= 1) {
                    this._land();
                    this.hud.prompt("");
                    this.hud.hot(false);
                    return;
                }
            } else {
                this._holdPose(pos, quat);
                this.held.position.copy(pos);
                this.held.quaternion.copy(quat);
            }
            // The disc stays inside the sleeve. It used to slide out once the sleeve
            // was turned to show the back, and that was never wanted: the back is for
            // reading. The front view never slid it out, so it now never shows. Set
            // outGoal to 1 under a condition here to bring it back.
            const outGoal = 0;
            this.outT += (outGoal - this.outT) * Math.min(1, dt * 3.2 / DISC_SLIDE_TIME);
            const out = smoothstep01(this.outT);
            this.disc.visible = out > .03;
            if (this.disc.visible) {
                this.disc.position.set(.215 * out, .02 * out, -.006);
                this.spinVel += (turning ? 5.5 : 0) * dt;
                this.spinVel *= Math.pow(.12, dt);
                this.spin += this.spinVel * dt;
                this.disc.rotation.set(Math.PI / 2, 0, 0);
                this.disc.rotateY(this.spin);
            }
            if (this.phase !== "drop") {
                const playable = this._playableTracks().length;
                this.hud.prompt((playable ? `F / 1-${Math.min(9,playable)} — 30s demo · ` : "") +
                    (this._isOwned(this.held.userData) ? "L — your studio · " : "C — take it · ") +
                    "R — turn it over · Q — put it back");
            } else this.hud.prompt("");
            this.hud.hot(false);
            if (this.player) this.player.leanTarget = null;
            return;
        }
        if (this.rack) {
            this.hud.hot(false);
            if (this.player) this.player.leanTarget = null;
            this.hud.prompt("A / D — flip through · E — pull it out · Q — step back");
            return;
        }
        this.ray.setFromCamera({
            x: 0,
            y: 0
        }, this.camera);
        const hits = this.ray.intersectObjects(PICKABLES, false);
        const hit = hits.length ? hits[0].object : null;
        if (hit !== this.hover) {
            this.hover = hit;
            this._setHover(hit);
            this.hud.hot(!!hit);
        }
        if (this.player) this.player.leanTarget = hits.length ? hits[0].point : null;
        if (hit) this.hud.prompt("E — pull it out");
        else if (this._nearCounter()) {
            this.hud.hot(true);
            this.hud.prompt(this.crate.length ? `E — buy ${this.crate.length} record${this.crate.length===1?"":"s"} · ${this.crate.length*RECORD_PRICE} credits · T — talk to the clerk` : "E — the counter · T — talk to the clerk");
        } else this.hud.prompt(this._findRack() ? "E — dig through this bin" : "");
    }
}

export { RECORD_PRICE, credits, requestGeneration, backCoverTexture, CrateInteractor };
